import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Users } from 'lucide-react';
import { supabase } from '@/lib/supabase/client';

interface AttendeesListProps {
  experienceId: string;
  className?: string;
}

interface Attendee {
  booking_id: string;
  user: {
    id: string;
    full_name: string;
    profile_image: string | null;
  };
  participant_count: number;
  created_at: string;
}

interface AttendeeData {
  total_attendees: number;
  confirmed_bookings: number;
  attendees: Attendee[];
}

export function AttendeesList({ experienceId, className }: AttendeesListProps) {
  const [attendeeData, setAttendeeData] = React.useState<AttendeeData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchAttendees = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .rpc('get_experience_attendees', { experience_id_param: experienceId });

      if (error) throw error;
      setAttendeeData(data);
    } catch (err) {
      console.error('Failed to fetch attendees:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch attendees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAttendees();

    // Subscribe to booking changes
    const subscription = supabase
      .channel(`bookings:${experienceId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bookings',
          filter: `experience_id=eq.${experienceId}`
        },
        () => {
          fetchAttendees();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [experienceId]);

  if (loading) {
    return (
      <div className={`${className} animate-pulse`}>
        <div className="h-32 bg-earth-800/50 rounded-lg"></div>
      </div>
    );
  }

  if (error) {
    return null;
  }

  if (!attendeeData || attendeeData.total_attendees === 0) {
    return (
      <div className={`${className} bg-earth-800/50 p-6 rounded-lg`}>
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-sand-400" />
          <h3 className="text-lg font-display">Who's Attending</h3>
        </div>
        <p className="text-sand-400 text-sm">
          Be the first to join this experience!
        </p>
      </div>
    );
  }

  return (
    <div className={`${className} bg-earth-800/50 p-6 rounded-lg`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Users className="h-5 w-5 text-sand-400" />
          <h3 className="text-lg font-display">Who's Attending</h3>
        </div>
        <div className="text-sm text-sand-400">
          {attendeeData.total_attendees} {attendeeData.total_attendees === 1 ? 'person' : 'people'} attending
        </div>
      </div>

      <div className="space-y-6">
        {/* Attendee Avatars */}
        <div className="relative h-12">
          <div className="flex -space-x-3 absolute">
            {attendeeData.attendees.slice(0, 5).map((attendee, index) => (
              <div 
                key={attendee.booking_id}
                className="relative"
                style={{ zIndex: 5 - index }}
              >
                <img
                  src={attendee.user.profile_image || `https://api.dicebear.com/7.x/initials/svg?seed=${attendee.user.full_name}`}
                  alt={attendee.user.full_name}
                  className="w-12 h-12 rounded-full border-2 border-earth-800 bg-earth-700"
                />
                {attendee.participant_count > 1 && (
                  <div className="absolute -top-1 -right-1 bg-sand-400/10 text-sand-300 text-xs rounded-full px-1.5 py-0.5 border border-earth-800">
                    +{attendee.participant_count - 1}
                  </div>
                )}
              </div>
            ))}
            {attendeeData.attendees.length > 5 && (
              <div className="w-12 h-12 rounded-full border-2 border-earth-800 bg-earth-700 flex items-center justify-center">
                <span className="text-sm text-sand-300">
                  +{attendeeData.attendees.length - 5}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="space-y-3 mt-8">
          <h4 className="text-sm font-medium text-sand-300">Recent Bookings</h4>
          {attendeeData.attendees.slice(0, 3).map((attendee) => (
            <div key={attendee.booking_id} className="flex items-center gap-3">
              <img
                src={attendee.user.profile_image || `https://api.dicebear.com/7.x/initials/svg?seed=${attendee.user.full_name}`}
                alt={attendee.user.full_name}
                className="w-8 h-8 rounded-full bg-earth-700"
              />
              <div className="flex-1">
                <p className="text-sm text-sand-100">
                  {attendee.user.full_name}
                  {attendee.participant_count > 1 && (
                    <span className="text-sand-400">
                      {' '}+{attendee.participant_count - 1} {attendee.participant_count - 1 === 1 ? 'guest' : 'guests'}
                    </span>
                  )}
                </p>
                <p className="text-xs text-sand-400">
                  Booked {new Date(attendee.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}